import axios from 'axios';

import {getConfig} from '../config';

class WebAuthService {

  constructor(config) {
    this.userPoolId = config.userPoolId;
    this.clientId = config.clientId;
    this.instance = axios.create({
      baseURL: "https://cognito-idp."+config.region+".amazonaws.com/",
      headers: {
        'Content-Type': 'application/x-amz-json-1.1'
      }
    });
  }

  parseJwt(token) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
  }

  login(username, password) {
    return new Promise((resolve, reject) => {
      this.instance.post('', {
        "AuthFlow": "USER_PASSWORD_AUTH",
        "AuthParameters": {
            "USERNAME": username,
            "PASSWORD": password
        },
        "ClientId": this.clientId
      },{
        headers: {
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
        }
      }).then(response => {
        if(response.data.AuthenticationResult){
          let jwt = this.parseJwt(response.data.AuthenticationResult.IdToken);
          resolve({
            "AccessToken": response.data.AuthenticationResult.AccessToken,
            "IdToken": response.data.AuthenticationResult.IdToken,
            "ExpiresIn": response.data.AuthenticationResult.ExpiresIn,
            "RefreshToken": response.data.AuthenticationResult.RefreshToken,
            "JwtToken": jwt,
            "Level": (jwt["cognito:groups"] && jwt["cognito:groups"].includes("CesiAdmin")) ? "Admin" : (jwt["cognito:groups"] && jwt["cognito:groups"].includes("CesiPublisher")) ? "Publisher" : (jwt["cognito:groups"] && jwt["cognito:groups"].includes("CesiEditor")) ? "Editor" : "User"
          });
        }else{
          reject(response.data);
        }
      }).catch(error => {
        reject(error.response.data);
      });
    });
  }

  refresh(refreshToken) {
    return new Promise((resolve, reject) => {
      this.instance.post('', {
        "AuthFlow": "REFRESH_TOKEN_AUTH",
        "AuthParameters": {
            "REFRESH_TOKEN": refreshToken
        },
        "ClientId": this.clientId
      },{
        headers: {
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.InitiateAuth'
        }
      }).then(response => {
        let jwt = this.parseJwt(response.data.AuthenticationResult.IdToken);
        resolve({
          "AccessToken": response.data.AuthenticationResult.AccessToken,
          "IdToken": response.data.AuthenticationResult.IdToken,
          "ExpiresIn": response.data.AuthenticationResult.ExpiresIn,
          "RefreshToken": response.data.AuthenticationResult.RefreshToken,
          "JwtToken": this.parseJwt(response.data.AuthenticationResult.IdToken),
          "Level": (jwt["cognito:groups"] && jwt["cognito:groups"].includes("CesiAdmin")) ? "Admin" : (jwt["cognito:groups"] && jwt["cognito:groups"].includes("CesiPublisher")) ? "Publisher" : (jwt["cognito:groups"] && jwt["cognito:groups"].includes("CesiEditor")) ? "Editor" : "User"
        });
      }).catch(error => {
        reject(error.response.data);
      });
    });
  }

  registration(username, password, givenName, familyName, locale) {
    return new Promise((resolve, reject) => {
      this.instance.post('', {
        "Username": username,
        "Password": password,
        "ClientId": getConfig("cognitoClientId"),
        "UserAttributes": [{
          "Name": "given_name",
          "Value": givenName
        },{
          "Name": "family_name",
          "Value": familyName
        },{
          "Name": "locale",
          "Value": locale
        }]
      },{
        headers: {
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.SignUp'
        }
      }).then(response => {
        resolve({
          "UserSub": response.data.UserSub,
          "UserConfirmed": response.data.UserConfirmed
        });
      }).catch(error => {
        reject(error.response.data);
      });
    });
  }

  emailValidation(username, code) {
    return new Promise((resolve, reject) => {
      this.instance.post('', {
        "ClientId": getConfig("cognitoClientId"),
        "ConfirmationCode": code,
        "Username": username
      },{
        headers: {
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmSignUp'
        }
      }).then(response => {
        resolve(true);
      }).catch(error => {
        reject(error.response.data);
      });
    });
  }

  setNewPassword(username, newPassword, code) {
    return new Promise((resolve, reject) => {
      this.instance.post('', {
        "ClientId": getConfig("cognitoClientId"),
        "Password": newPassword,
        "ConfirmationCode": code,
        "Username": username
      },{
        headers: {
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ConfirmForgotPassword'
        }
      }).then(response => {
        resolve(true);
      }).catch(error => {
        reject(error.response.data);
      });
    });
  }

  forgotPassword(username) {
    return new Promise((resolve, reject) => {
      this.instance.post('', {
        "ClientId": getConfig("cognitoClientId"),
        "Username": username
      },{
        headers: {
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ForgotPassword'
        }
      }).then(response => {
        resolve(true);
      }).catch(error => {
        reject(error.response.data);
      });
    });
  }

  getUser(accessToken) {
    return new Promise((resolve, reject) => {
      this.instance.post('', {
        "AccessToken": accessToken
      },{
        headers: {
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.GetUser'
        }
      }).then(response => {
        resolve(response);
      }).catch(error => {
        reject(error.response.data);
      });
    });
  }

  changePassword(oldPassword, newPassword, accessToken) {
    return new Promise((resolve, reject) => {
      this.instance.post('', {
        "AccessToken": accessToken,
        "PreviousPassword": oldPassword,
        "ProposedPassword": newPassword
      },{
        headers: {
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.ChangePassword'
        }
      }).then(response => {
        resolve(true);
      }).catch(error => {
        reject(error.response.data);
      });
    });
  }

  newPasswordChallenge(username, password, session) {
    return new Promise((resolve, reject) => {
      this.instance.post('', {
        "ClientId": getConfig("cognitoClientId"),
        "ChallengeName": "NEW_PASSWORD_REQUIRED",
        "ChallengeResponses": {
          "USERNAME": username,
          "NEW_PASSWORD": password
        },
        "Session": session
      },{
        headers: {
          'X-Amz-Target': 'AWSCognitoIdentityProviderService.RespondToAuthChallenge'
        }
      }).then(response => {
        if(response.data.AuthenticationResult){
          let jwt = this.parseJwt(response.data.AuthenticationResult.IdToken);
          resolve({
            "AccessToken": response.data.AuthenticationResult.AccessToken,
            "IdToken": response.data.AuthenticationResult.IdToken,
            "ExpiresIn": response.data.AuthenticationResult.ExpiresIn,
            "RefreshToken": response.data.AuthenticationResult.RefreshToken,
            "JwtToken": this.parseJwt(response.data.AuthenticationResult.IdToken),
            "Level": (jwt["cognito:groups"] && jwt["cognito:groups"].includes("CesiAdmin")) ? "Admin" : (jwt["cognito:groups"] && jwt["cognito:groups"].includes("CesiPublisher")) ? "Publisher" : (jwt["cognito:groups"] && jwt["cognito:groups"].includes("CesiEditor")) ? "Editor" : "User"
          });
        }else{
          reject(response.data);
        }
      }).catch(error => {
        reject(error.response.data);
      });
    });
  }
}


export default WebAuthService;
